import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Bot, 
  Send, 
  Loader2, 
  Sparkles,
  BarChart3,
  PieChart,
  TrendingUp,
  Users,
  ClipboardList,
  Lightbulb,
  Trash2,
  RefreshCw,
  Activity,
  Target,
  UserCheck,
  Building2,
  ChevronRight,
  Settings2,
  PanelRightOpen,
  PanelRightClose,
  Calendar,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

const CHART_COLORS = ['#E63946', '#7FB285', '#457B9D', '#F4A261', '#2A9D8F', '#E9C46A', '#264653', '#A8DADC'];

// Topics for selection
const ANALYSIS_TOPICS = {
  graficos: [
    { id: 'internacoes', label: 'Internações', icon: Building2 },
    { id: 'tarefas', label: 'Tarefas', icon: ClipboardList },
    { id: 'sla', label: 'SLA / Prazos', icon: Target },
    { id: 'profissionais', label: 'Profissionais', icon: Users },
    { id: 'altas', label: 'Altas', icon: TrendingUp },
  ],
  insights: [
    { id: 'eficiencia', label: 'Eficiência Operacional', icon: Activity },
    { id: 'custos', label: 'Redução de Custos', icon: BarChart3 },
    { id: 'qualidade', label: 'Qualidade do Atendimento', icon: UserCheck },
    { id: 'tendencias', label: 'Tendências', icon: TrendingUp },
    { id: 'alertas', label: 'Alertas e Riscos', icon: Sparkles },
  ]
};

// CustomTooltip component - defined outside to avoid re-creation on each render
const ChartTooltip = ({ active, payload, label, isClickable }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border">
        <p className="font-medium">{label || payload[0]?.name}</p>
        <p className="text-sm text-muted-foreground">
          Valor: <span className="font-semibold text-primary">{payload[0]?.value}</span>
        </p>
        {isClickable && (
          <p className="text-xs text-blue-500 mt-1">Clique para ver detalhes</p>
        )}
      </div>
    );
  }
  return null;
};

const AgenteIA = () => {
  const { api, user, canUseAI, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [rightPanelTab, setRightPanelTab] = useState('graficos');
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [customRequest, setCustomRequest] = useState('');
  const [quickCharts, setQuickCharts] = useState([]);
  const [quickInsights, setQuickInsights] = useState([]);
  const [loadingQuick, setLoadingQuick] = useState(false);
  const [dashboardMetrics, setDashboardMetrics] = useState([]);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  // Predictive Analytics state
  const [predictiveData, setPredictiveData] = useState(null);
  const [loadingPredictive, setLoadingPredictive] = useState(false);
  const messagesEndRef = useRef(null);

  // Redirect if user doesn't have permission
  useEffect(() => {
    if (!authLoading && !canUseAI && !isAdmin) {
      toast.error('Você não tem permissão para acessar a IA Business Intelligence');
      navigate('/dashboard');
    }
  }, [authLoading, canUseAI, isAdmin, navigate]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load dashboard metrics on mount
  useEffect(() => {
    loadDashboardMetrics();
    loadPredictiveAnalytics();
  }, []);

  // Load initial quick analyses
  useEffect(() => {
    loadQuickAnalyses();
  }, []);

  const loadPredictiveAnalytics = async () => {
    setLoadingPredictive(true);
    try {
      const response = await api.get('/ai-agent/predictive-analytics');
      setPredictiveData(response.data);
    } catch (err) {
      console.error('Error loading predictive analytics:', err);
    } finally {
      setLoadingPredictive(false);
    }
  };

  const loadDashboardMetrics = async () => {
    setLoadingMetrics(true);
    try {
      const response = await api.get('/ai-agent/dashboard-metrics');
      setDashboardMetrics(response.data.metrics || []);
    } catch (err) {
      console.error('Error loading dashboard metrics:', err);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const loadQuickAnalyses = async () => {
    setLoadingQuick(true);
    try {
      // Load initial charts and insights
      const [chartsRes, insightsRes] = await Promise.all([
        api.post('/ai-agent/quick-analysis', { analysis_type: 'admissions_overview' }),
        api.post('/ai-agent/quick-analysis', { analysis_type: 'tasks_sla' })
      ]);
      
      if (chartsRes.data.chart_data) {
        setQuickCharts([chartsRes.data.chart_data]);
      }
      if (insightsRes.data.insights) {
        setQuickInsights(insightsRes.data.insights);
      }
    } catch (err) {
      console.error('Error loading quick analyses:', err);
    } finally {
      setLoadingQuick(false);
    }
  };

  const handleTopicToggle = (topicId) => {
    setSelectedTopics(prev => 
      prev.includes(topicId) 
        ? prev.filter(t => t !== topicId)
        : [...prev, topicId]
    );
  };

  const applyTopicSelection = async () => {
    if (selectedTopics.length === 0 && !customRequest.trim()) {
      toast.error('Selecione pelo menos um tópico ou escreva um pedido');
      return;
    }

    const topics = selectedTopics.map(id => {
      const allTopics = [...ANALYSIS_TOPICS.graficos, ...ANALYSIS_TOPICS.insights];
      return allTopics.find(t => t.id === id)?.label;
    }).filter(Boolean).join(', ');

    let prompt = '';
    if (rightPanelTab === 'graficos') {
      prompt = `Gere gráficos detalhados sobre: ${topics}. ${customRequest}`;
    } else {
      prompt = `Forneça insights de Business Intelligence sobre: ${topics}. ${customRequest}`;
    }

    setLoadingQuick(true);
    try {
      const response = await api.post('/ai-agent/chat', { 
        message: prompt,
        session_id: sessionId 
      });
      
      if (response.data.chart_data) {
        setQuickCharts(prev => [response.data.chart_data, ...prev].slice(0, 5));
      }
      if (response.data.insights) {
        setQuickInsights(prev => [...response.data.insights, ...prev].slice(0, 10));
      }
      
      // Also add to chat
      setMessages(prev => [
        ...prev,
        { role: 'user', content: prompt },
        { 
          role: 'assistant', 
          content: response.data.response,
          chart_data: response.data.chart_data,
          insights: response.data.insights
        }
      ]);
      
      if (!sessionId) {
        setSessionId(response.data.session_id);
      }
      
      setSelectedTopics([]);
      setCustomRequest('');
      toast.success('Análise gerada com sucesso!');
    } catch (err) {
      toast.error('Erro ao gerar análise');
    } finally {
      setLoadingQuick(false);
    }
  };

  const sendMessage = async (message) => {
    if (!message.trim()) return;

    setMessages(prev => [...prev, { role: 'user', content: message }]);
    setInputMessage('');
    setLoading(true);

    try {
      const response = await api.post('/ai-agent/chat', { 
        message, 
        session_id: sessionId 
      });
      
      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        chart_data: response.data.chart_data,
        insights: response.data.insights
      }]);

      // Update quick panels if new data
      if (response.data.chart_data) {
        setQuickCharts(prev => [response.data.chart_data, ...prev].slice(0, 5));
      }
      if (response.data.insights) {
        setQuickInsights(prev => [...response.data.insights, ...prev].slice(0, 10));
      }

    } catch (err) {
      toast.error('Erro ao comunicar com o agente de IA');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Desculpe, ocorreu um erro ao processar sua solicitação. Por favor, tente novamente.',
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearSession = async () => {
    if (!sessionId) return;
    
    try {
      await api.delete(`/ai-agent/session/${sessionId}`);
      setMessages([]);
      setSessionId(null);
      setQuickCharts([]);
      setQuickInsights([]);
      loadQuickAnalyses();
      toast.success('Conversa limpa com sucesso');
    } catch (err) {
      toast.error('Erro ao limpar conversa');
    }
  };

  // Chart click handler - navigates to filtered data
  const handleChartClick = (data, chartType, chartTitle) => {
    if (!data || !data.name) return;
    
    const label = data.name.toLowerCase();
    
    // Determine navigation based on chart context
    if (chartTitle?.includes('Internações') || chartTitle?.includes('Origem')) {
      // Navigate to internações list with filter
      if (label.includes('urgência') || label.includes('eletivo') || label.includes('uti')) {
        const origemMap = {
          'urgência própria': 'urgencia_propria',
          'urgência terceiro': 'urgencia_terceiro',
          'eletivo clínica': 'eletivo_clinica',
          'eletivo cirúrgico': 'eletivo_cirurgico',
          'uti': 'uti'
        };
        const origem = origemMap[label] || label;
        navigate(`/lista?origem=${origem}`);
        toast.info(`Filtrando internações por: ${data.name}`);
      } else {
        navigate('/lista');
      }
    } else if (chartTitle?.includes('Tarefa') || chartTitle?.includes('SLA')) {
      // Navigate to tarefas list with filter
      if (label.includes('concluída') || label.includes('concluida')) {
        navigate('/tarefas?status=concluida');
      } else if (label.includes('fazer') || label.includes('pendente')) {
        navigate('/tarefas?status=a_fazer');
      } else if (label.includes('vencid')) {
        navigate('/tarefas?vencidas=true');
      } else if (label.includes('andamento')) {
        navigate('/tarefas?status=em_andamento');
      } else {
        navigate('/tarefas');
      }
      toast.info(`Filtrando tarefas por: ${data.name}`);
    } else if (chartTitle?.includes('Alta')) {
      // Navigate to altas list
      navigate('/lista?tab=altas');
      toast.info(`Visualizando altas`);
    } else {
      // Default navigation
      navigate('/estatisticas');
    }
  };

  const renderChart = (chartData, isClickable = true) => {
    if (!chartData) return null;

    const { type, title, data, xKey = 'name', yKey = 'value' } = chartData;

    return (
      <Card className="bg-white dark:bg-gray-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            {type === 'bar' && (
              <BarChart data={data} onClick={(e) => isClickable && e?.activePayload && handleChartClick(e.activePayload[0]?.payload, 'bar', title)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={xKey} fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip content={<ChartTooltip isClickable={isClickable} />} />
                <Bar dataKey={yKey} radius={[4, 4, 0, 0]} className="cursor-pointer">
                  {data.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                      className="hover:opacity-80 transition-opacity"
                    />
                  ))}
                </Bar>
              </BarChart>
            )}
            {type === 'line' && (
              <LineChart data={data} onClick={(e) => isClickable && e?.activePayload && handleChartClick(e.activePayload[0]?.payload, 'line', title)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={xKey} fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip content={<ChartTooltip isClickable={isClickable} />} />
                <Line type="monotone" dataKey={yKey} stroke="#E63946" strokeWidth={2} dot={{ fill: '#E63946', cursor: 'pointer' }} />
              </LineChart>
            )}
            {type === 'pie' && (
              <RechartsPie>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={70}
                  dataKey={yKey}
                  onClick={(e) => isClickable && handleChartClick(e, 'pie', title)}
                  className="cursor-pointer"
                >
                  {data.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                      className="hover:opacity-80 transition-opacity"
                    />
                  ))}
                </Pie>
                <Tooltip content={<ChartTooltip isClickable={isClickable} />} />
              </RechartsPie>
            )}
            {type === 'area' && (
              <AreaChart data={data} onClick={(e) => isClickable && e?.activePayload && handleChartClick(e.activePayload[0]?.payload, 'area', title)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={xKey} fontSize={11} />
                <YAxis fontSize={11} />
                <Tooltip content={<ChartTooltip isClickable={isClickable} />} />
                <Area type="monotone" dataKey={yKey} stroke="#7FB285" fill="#7FB285" fillOpacity={0.3} className="cursor-pointer" />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </CardContent>
      </Card>
    );
  };

  const renderInsights = (insights) => {
    if (!insights || insights.length === 0) return null;

    return (
      <div className="space-y-2">
        {insights.map((insight, index) => (
          <div 
            key={index} 
            className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
          >
            <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
              <Lightbulb className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
              <span>{insight}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMessage = (message, index) => {
    const isUser = message.role === 'user';
    
    return (
      <div 
        key={index}
        className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
          <div className={`flex items-start gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
              isUser 
                ? 'bg-primary text-primary-foreground' 
                : message.isError 
                  ? 'bg-red-500 text-white'
                  : 'bg-gradient-to-br from-emerald-500 to-green-600 text-white'
            }`}>
              {isUser ? user?.nome?.charAt(0)?.toUpperCase() || 'U' : <Bot className="h-5 w-5" />}
            </div>
            <div className={`rounded-2xl px-4 py-3 ${
              isUser 
                ? 'bg-primary text-primary-foreground rounded-tr-none' 
                : message.isError
                  ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200 rounded-tl-none'
                  : 'bg-muted rounded-tl-none'
            }`}>
              <div className="whitespace-pre-wrap text-sm leading-relaxed">
                {message.content}
              </div>
            </div>
          </div>
          
          {/* Render chart if present */}
          {message.chart_data && (
            <div className="mt-3">
              {renderChart(message.chart_data)}
            </div>
          )}
          
          {/* Render insights if present */}
          {message.insights && (
            <div className="mt-3">
              {renderInsights(message.insights)}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="flex h-[calc(100vh-120px)] gap-4" data-testid="agente-ia-page">
        {/* Main Chat Area */}
        <div className={`flex-1 flex flex-col ${rightPanelOpen ? 'lg:mr-0' : ''}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 text-white">
                  <Bot className="h-5 w-5" />
                </div>
                IA Business Intelligence
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Análise inteligente de dados e insights para tomada de decisão
              </p>
            </div>
            <div className="flex items-center gap-2">
              {sessionId && (
                <Button variant="outline" size="sm" onClick={clearSession}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Nova Conversa
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setRightPanelOpen(!rightPanelOpen)}
                className="lg:hidden"
              >
                {rightPanelOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Chat Card */}
          <Card className="flex-1 flex flex-col overflow-hidden">
            <CardContent className="flex-1 flex flex-col p-4 overflow-hidden">
              {/* Messages */}
              <ScrollArea className="flex-1 pr-4">
                {messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground py-4">
                    {/* Dashboard Metrics Cards */}
                    <div className="w-full max-w-3xl mb-6">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {loadingMetrics ? (
                          // Loading skeleton
                          [...Array(4)].map((_, i) => (
                            <div key={i} className="bg-muted/50 rounded-lg p-4 animate-pulse">
                              <div className="h-4 w-16 bg-muted rounded mb-2" />
                              <div className="h-8 w-20 bg-muted rounded mb-1" />
                              <div className="h-3 w-24 bg-muted rounded" />
                            </div>
                          ))
                        ) : (
                          dashboardMetrics.map((metric) => {
                            const IconComponent = {
                              Calendar: Calendar,
                              Users: Users,
                              AlertTriangle: AlertTriangle,
                              Target: Target
                            }[metric.icon] || Activity;
                            
                            const colorClasses = {
                              emerald: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300',
                              blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300',
                              red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300',
                              green: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300',
                              amber: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'
                            }[metric.color] || 'bg-gray-50 dark:bg-gray-900/20';
                            
                            const iconColorClasses = {
                              emerald: 'text-emerald-600',
                              blue: 'text-blue-600',
                              red: 'text-red-600',
                              green: 'text-green-600',
                              amber: 'text-amber-600'
                            }[metric.color] || 'text-gray-600';
                            
                            return (
                              <div 
                                key={metric.id} 
                                className={`rounded-lg p-4 border ${colorClasses} transition-all hover:shadow-md cursor-pointer`}
                                onClick={() => {
                                  // Click on metric to ask AI about it
                                  const questions = {
                                    total_diarias: 'Analise o total de diárias e me dê insights sobre a evolução',
                                    internacoes_ativas: 'Quais são os detalhes das internações ativas? Mostre um resumo por origem',
                                    tarefas_vencidas: 'Analise as tarefas vencidas e sugira ações para melhorar o SLA',
                                    sla_rate: 'Detalhe a taxa de SLA e identifique os principais gargalos'
                                  };
                                  sendMessage(questions[metric.id] || `Analise a métrica ${metric.label}`);
                                }}
                                data-testid={`metric-${metric.id}`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <IconComponent className={`h-4 w-4 ${iconColorClasses}`} />
                                  <span className="text-xs font-medium opacity-80">{metric.label}</span>
                                </div>
                                <div className="text-2xl font-bold">{metric.value}</div>
                                <div className="text-xs opacity-70">{metric.description}</div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 text-center">
                        Clique em uma métrica para obter análise detalhada
                      </p>
                    </div>
                    
                    {/* Predictive Analytics Alerts */}
                    {predictiveData && (predictiveData.risk_alerts?.length > 0 || predictiveData.predictions?.length > 0) && (
                      <div className="w-full max-w-3xl mb-4">
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-500" />
                          Alertas Preditivos
                        </h4>
                        <div className="space-y-2">
                          {predictiveData.risk_alerts?.slice(0, 3).map((alert, idx) => (
                            <div 
                              key={idx}
                              className={`p-3 rounded-lg border text-sm ${
                                alert.severity === 'alta' 
                                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
                                  : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200'
                              }`}
                            >
                              <div className="font-medium">{alert.title}</div>
                              <div className="text-xs opacity-80 mt-1">{alert.description}</div>
                              {alert.action && (
                                <div className="text-xs mt-1 font-medium">Ação: {alert.action}</div>
                              )}
                            </div>
                          ))}
                          {predictiveData.predictions?.slice(0, 2).map((pred, idx) => (
                            <div 
                              key={`pred-${idx}`}
                              className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 text-sm"
                            >
                              <div className="font-medium">{pred.title}</div>
                              <div className="text-xs opacity-80 mt-1">{pred.description}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Operational Insights */}
                    {predictiveData?.operational_insights?.length > 0 && (
                      <div className="w-full max-w-3xl mb-4">
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                          <Lightbulb className="h-4 w-4 text-emerald-500" />
                          Insights Operacionais
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {predictiveData.operational_insights.map((insight, idx) => (
                            <div 
                              key={idx}
                              className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-sm"
                            >
                              <div className="font-medium text-emerald-800 dark:text-emerald-200">{insight.title}</div>
                              <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">{insight.description}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Welcome message */}
                    <div className="p-3 rounded-full bg-gradient-to-br from-emerald-500/10 to-green-600/10 mb-3">
                      <Bot className="h-8 w-8 text-emerald-600" />
                    </div>
                    <h3 className="text-base font-semibold mb-2">Olá! Sou a IA Business Intelligence</h3>
                    <p className="max-w-md mb-3 text-sm">
                      Analiso dados, gero gráficos e forneço insights estratégicos para sua operação.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                      <Badge variant="secondary" className="text-xs">📊 Gráficos automáticos</Badge>
                      <Badge variant="secondary" className="text-xs">💡 Insights de BI</Badge>
                      <Badge variant="secondary" className="text-xs">📈 Métricas de SLA</Badge>
                      <Badge variant="secondary" className="text-xs">🔮 Analytics Preditivos</Badge>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 py-4">
                    {messages.map((msg, idx) => renderMessage(msg, idx))}
                    {loading && (
                      <div className="flex justify-start mb-4">
                        <div className="flex items-start gap-2">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-green-600 text-white">
                            <Bot className="h-5 w-5" />
                          </div>
                          <div className="rounded-2xl rounded-tl-none bg-muted px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span className="text-sm">Analisando dados...</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <div className="pt-4 border-t mt-auto">
                <form 
                  onSubmit={(e) => { e.preventDefault(); sendMessage(inputMessage); }}
                  className="flex gap-2"
                >
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Faça uma pergunta sobre os dados..."
                    disabled={loading}
                    className="flex-1"
                    data-testid="ai-chat-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={loading || !inputMessage.trim()}
                    data-testid="ai-chat-send"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </form>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Powered by GPT-4o-mini • Clique nos gráficos para ver detalhes
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Análises Rápidas */}
        <div className={`
          ${rightPanelOpen ? 'w-80 lg:w-96' : 'w-0'} 
          transition-all duration-300 overflow-hidden
          flex-shrink-0
        `}>
          <Card className="h-full flex flex-col">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-emerald-500" />
                Análises Rápidas
              </CardTitle>
              <CardDescription className="text-xs">
                Conteúdo evolui conforme a IA aprende
              </CardDescription>
            </CardHeader>
            
            {/* Tabs */}
            <Tabs value={rightPanelTab} onValueChange={setRightPanelTab} className="flex-1 flex flex-col">
              <div className="px-4">
                <TabsList className="w-full">
                  <TabsTrigger value="graficos" className="flex-1 text-xs">
                    <BarChart3 className="h-3 w-3 mr-1" />
                    Gráficos
                  </TabsTrigger>
                  <TabsTrigger value="insights" className="flex-1 text-xs">
                    <Lightbulb className="h-3 w-3 mr-1" />
                    Insights
                  </TabsTrigger>
                </TabsList>
              </div>

              <CardContent className="flex-1 overflow-hidden p-4 pt-3">
                <ScrollArea className="h-full">
                  {/* Topic Selection */}
                  <div className="mb-4">
                    <Label className="text-xs font-medium mb-2 block">Selecionar tópicos</Label>
                    <div className="flex flex-wrap gap-2">
                      {ANALYSIS_TOPICS[rightPanelTab].map((topic) => (
                        <label 
                          key={topic.id}
                          className={`
                            flex items-center gap-1.5 px-2 py-1 rounded-full text-xs cursor-pointer
                            transition-all border
                            ${selectedTopics.includes(topic.id) 
                              ? 'bg-emerald-500 text-white border-emerald-500' 
                              : 'bg-muted hover:bg-muted/80 border-transparent'
                            }
                          `}
                        >
                          <Checkbox 
                            checked={selectedTopics.includes(topic.id)}
                            onCheckedChange={() => handleTopicToggle(topic.id)}
                            className="hidden"
                          />
                          <topic.icon className="h-3 w-3" />
                          {topic.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Custom Request */}
                  <div className="mb-4">
                    <Label className="text-xs font-medium mb-2 block">Escrever pedido</Label>
                    <Textarea
                      value={customRequest}
                      onChange={(e) => setCustomRequest(e.target.value)}
                      placeholder="Descreva o que deseja analisar..."
                      className="text-xs h-16 resize-none"
                      data-testid="custom-request-input"
                    />
                  </div>

                  {/* Apply Button */}
                  <Button 
                    size="sm" 
                    className="w-full mb-4 bg-emerald-600 hover:bg-emerald-700"
                    onClick={applyTopicSelection}
                    disabled={loadingQuick}
                    data-testid="apply-analysis-btn"
                  >
                    {loadingQuick ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Sparkles className="h-4 w-4 mr-2" />
                    )}
                    Aplicar
                  </Button>

                  {/* Tab Content */}
                  <TabsContent value="graficos" className="mt-0 space-y-3">
                    {loadingQuick && quickCharts.length === 0 ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
                      </div>
                    ) : quickCharts.length > 0 ? (
                      quickCharts.map((chart, idx) => (
                        <div key={idx}>
                          {renderChart(chart, true)}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>Selecione tópicos ou escreva um pedido para gerar gráficos</p>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="insights" className="mt-0">
                    {loadingQuick && quickInsights.length === 0 ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
                      </div>
                    ) : quickInsights.length > 0 ? (
                      renderInsights(quickInsights)
                    ) : (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        <Lightbulb className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>Selecione tópicos ou escreva um pedido para gerar insights</p>
                      </div>
                    )}
                  </TabsContent>
                </ScrollArea>
              </CardContent>
            </Tabs>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default AgenteIA;
